// CreateMemberBodyType.jsx
import { useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { toast } from "react-toastify";

import api from "@/utils/axios";
import i18n from "@/i18n";
import cls from "./CreateMemberBodyType.module.scss";
import { BODY_TYPES } from "@/enums/lifestyleEnums";
import { useAuth } from "@/app/auth-context-state";

/* Welcome-style hero placeholders (q12) */
import mrImg from "@/assets/UI/memberCreate/q12Mr.webp";
import mrsImg from "@/assets/UI/memberCreate/q12Mrs.webp";
import mrMImg from "@/assets/UI/memberCreate/q12MrM.webp";
import mrsMImg from "@/assets/UI/memberCreate/q12MrsM.webp";

/* After-form cards images (q12) */
import fitAndActionImg from "@/assets/UI/memberCreate/q12fitAndAction.webp";
import curvyImg from "@/assets/UI/memberCreate/q12curvy.webp";
import naturalImg from "@/assets/UI/memberCreate/q12natural.webp";
import athleticImg from "@/assets/UI/memberCreate/q12athletic.webp";
import progressImg from "@/assets/UI/memberCreate/q12progress.webp";

/* ---- Framer animations (same feel as CreateMemberSexualRole) ---- */
const floatSlow = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.55, 0.72, 0.55],
    scale: [1, 1.03, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.72, 0.58, 0.72],
    scale: [1.03, 1, 1.03],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

/* Outlet context hook (same onboarding pattern) */
function useMemberLayoutContext() {
  return useOutletContext();
}

/* Emoji meta per body type */
const BODY_META = {
  fit_active: "💪",
  curvy_confident: "🍑",
  natural_soft: "🌸",
  athletic_sensual: "🏃‍♀️",
  in_progress: "🌱",
  not_select: "🫧",
};

/* After-form cards */
const CARD_IMAGES = {
  fit_active: fitAndActionImg,
  curvy_confident: curvyImg,
  natural_soft: naturalImg,
  athletic_sensual: athleticImg,
  in_progress: progressImg,
};

function getClosingKey({ isCouple, rawGender }) {
  if (isCouple) return "onboarding.body_type.closing.couple";

  if (rawGender === "man" || rawGender === "trans_man") {
    return "onboarding.body_type.closing.man";
  }
  if (rawGender === "woman" || rawGender === "trans_woman") {
    return "onboarding.body_type.closing.woman";
  }
  return "onboarding.body_type.closing.other";
}

export default function CreateMemberBodyType() {
  const navigate = useNavigate();
  const { refreshMe } = useAuth();

  const layoutCtx = useMemberLayoutContext() || {};
  const { memberType, isCouple: isCoupleCtx, rawGender } = layoutCtx;
  const isCouple = Boolean(isCoupleCtx || memberType === "couple");

  const [submitting, setSubmitting] = useState(false);
  const [memberChoice, setMemberChoice] = useState("");
  const [partnerChoice, setPartnerChoice] = useState("");

  const MrD = useMemo(() => mrImg, []);
  const MrsD = useMemo(() => mrsImg, []);
  const MrM = useMemo(() => mrMImg, []);
  const MrsM = useMemo(() => mrsMImg, []);

  const t = (k, o) => i18n.t(k, o);

  const title = t("onboarding.body_type.title", {
    defaultValue: "Body type",
  });

  const subtitle = !isCouple
    ? t("onboarding.body_type.single.question", {
        defaultValue: "Which body vibe feels most like you?",
      })
    : t("onboarding.body_type.couple.lead", {
        defaultValue: "Choose one for you, and one for your partner.",
      });

  const coupleDetail = isCouple
    ? t("onboarding.body_type.couple.detail", {
        defaultValue: "This helps us match preferences and keep things smooth.",
      })
    : "";

  const partnerLabel = t("onboarding.body_type.partner.label", {
    defaultValue: "Now pick your partner’s body type",
  });

  const hintFallback =
    t("onboarding.body_type.tapToSelect", {
      defaultValue: "Tap to choose the body vibe that feels most like you ✨",
    }) || "Tap to choose";

  const types =
    Array.isArray(BODY_TYPES) && BODY_TYPES.length
      ? BODY_TYPES
      : [
          "fit_active",
          "curvy_confident",
          "natural_soft",
          "athletic_sensual",
          "in_progress",
          "not_select",
        ];

  const options = types
    .filter((v) => v !== "not_select")
    .map((value) => {
      const labelKey = `onboarding.body_type.options.${value}.label`;
      const hintKey = `onboarding.body_type.options.${value}.hint`;

      return {
        value,
        emoji: BODY_META[value] || "✨",
        title: t(labelKey, { defaultValue: value }),
        hint: t(hintKey, { defaultValue: "" }) || hintFallback,
      };
    });

  const cardKeys = types.filter((v) => v !== "not_select");
  const cards = cardKeys.map((key) => ({
    key,
    img: CARD_IMAGES[key],
    emoji: BODY_META[key] || "✨",
    title: t(`onboarding.body_type.cards.${key}.title`, {
      defaultValue: t(`onboarding.body_type.options.${key}.label`, {
        defaultValue: key,
      }),
    }),
    desc: t(`onboarding.body_type.cards.${key}.desc`, { defaultValue: "" }),
  }));

  const closingKey =
    memberChoice && (!isCouple || partnerChoice)
      ? getClosingKey({ isCouple, rawGender })
      : null;

  const closingText = closingKey ? t(closingKey, { defaultValue: "" }) : "";

  async function onSubmit(e) {
    e.preventDefault();

    if (!memberChoice) {
      toast.info(
        t("onboarding.body_type.toast.member_required", {
          defaultValue: "Please pick one for you.",
        }),
      );
      return;
    }

    if (isCouple && !partnerChoice) {
      toast.info(
        t("onboarding.body_type.toast.partner_required", {
          defaultValue: "Please pick one for your partner.",
        }),
      );
      return;
    }

    const payload = isCouple
      ? { memberBodyType: memberChoice, partnerBodyType: partnerChoice }
      : { memberBodyType: memberChoice };

    setSubmitting(true);
    try {
      const { data } = await api.post("/v1/member-create/body_type", payload, {
        skipToast: true,
      });

      await refreshMe();
      navigate(data?.next || "/member/body_attract", { replace: true });
    } catch (err) {
      console.error("update body type failed:", err);
      toast.error(
        t("onboarding.body_type.toast.save_error", {
          defaultValue: "Something went wrong.",
        }),
      );
    } finally {
      setSubmitting(false);
    }
  }

  const submitDisabled =
    submitting || !memberChoice || (isCouple && !partnerChoice);

  return (
    <main className={cls.page}>
      {/* Fixed background stage */}
      <section className={cls.heroStage} aria-hidden="true">
        <div className={cls.mobileOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrM}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsM}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.desktopOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrD}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsD}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.stageTint} />
      </section>

      {/* Single-page scroll content (no nested scroll) */}
      <div className={cls.pageInner}>
        <div className={cls.formCard}>
          <div className={cls.formTop}>
            <div className={cls.formMeta}>
              <div className={cls.formTitle}>{title}</div>
              <div className={cls.formSub}>{subtitle}</div>
              {isCouple && coupleDetail ? (
                <div className={cls.formSub} style={{ marginTop: 8 }}>
                  {coupleDetail}
                </div>
              ) : null}
            </div>
          </div>

          <form className={cls.form} onSubmit={onSubmit} noValidate>
            {/* Member options */}
            <fieldset className={cls.options}>
              <legend className="sr-only">{subtitle}</legend>

              {options.map((opt) => {
                const checked = memberChoice === opt.value;

                return (
                  <label
                    key={`m-${opt.value}`}
                    className={`${cls.option} ${checked ? cls.optionActive : ""}`}
                  >
                    <input
                      type="radio"
                      name="memberBodyType"
                      value={opt.value}
                      checked={checked}
                      onChange={() => setMemberChoice(opt.value)}
                    />

                    <span className={cls.optionBody}>
                      <span className={cls.optionEmoji} aria-hidden="true">
                        {opt.emoji}
                      </span>

                      <span className={cls.optionText}>
                        <strong>{opt.title}</strong>
                        <small>{opt.hint}</small>
                      </span>

                      <span className={cls.optionDot} aria-hidden="true" />
                    </span>
                  </label>
                );
              })}
            </fieldset>

            {/* Partner options (couple only) */}
            {isCouple && (
              <div className={cls.partnerBlock}>
                <div className={cls.partnerTitle}>{partnerLabel}</div>

                <fieldset className={cls.options}>
                  <legend className="sr-only">{partnerLabel}</legend>

                  {options.map((opt) => {
                    const checked = partnerChoice === opt.value;

                    return (
                      <label
                        key={`p-${opt.value}`}
                        className={`${cls.option} ${checked ? cls.optionActive : ""}`}
                      >
                        <input
                          type="radio"
                          name="partnerBodyType"
                          value={opt.value}
                          checked={checked}
                          onChange={() => setPartnerChoice(opt.value)}
                        />

                        <span className={cls.optionBody}>
                          <span className={cls.optionEmoji} aria-hidden="true">
                            {opt.emoji}
                          </span>

                          <span className={cls.optionText}>
                            <strong>{opt.title}</strong>
                            <small>{opt.hint}</small>
                          </span>

                          <span className={cls.optionDot} aria-hidden="true" />
                        </span>
                      </label>
                    );
                  })}
                </fieldset>
              </div>
            )}

            {closingText ? (
              <div className={cls.closingNote} aria-live="polite">
                {closingText.split("\n").map((line) => (
                  <p key={line}>{line}</p>
                ))}
              </div>
            ) : null}

            {/* Submit */}
            <div className={cls.actions}>
              <button
                type="submit"
                className={cls.submit}
                disabled={submitDisabled}
                aria-busy={submitting ? "true" : "false"}
              >
                {submitting
                  ? t("common.saving", { defaultValue: "Saving…" })
                  : t("common.continue", { defaultValue: "Continue" })}
              </button>
            </div>
          </form>

          {/* After-form cards */}
          <section
            className={cls.cardsSection}
            aria-label="Body type guide cards"
          >
            <div className={cls.cardsHeader}>
              <div className={cls.cardsTitle}>
                {t("onboarding.body_type.guide.title", {
                  defaultValue: "Quick guide (if you’re curious)",
                })}
              </div>
              <div className={cls.cardsSub}>
                {t("onboarding.body_type.guide.body", {
                  defaultValue:
                    "Pick what feels right above. These are simple explanations to make it easier.",
                })}
              </div>
            </div>

            <div className={cls.cardsGrid}>
              {cards.map((c) => (
                <article key={c.key} className={cls.gCard}>
                  <div className={cls.gCardMedia}>
                    <img
                      className={cls.gCardImg}
                      src={c.img}
                      alt=""
                      loading="lazy"
                      draggable="false"
                    />
                    <div className={cls.gCardTint} />
                    <div className={cls.gCardIcon} aria-hidden="true">
                      <span className={cls.cardEmoji}>{c.emoji}</span>
                    </div>
                  </div>

                  <div className={cls.gCardBody}>
                    <div className={cls.gCardTitle}>{c.title}</div>
                    <div className={cls.gCardDesc}>{c.desc}</div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </div>

        <div className={cls.bottomSpace} />
      </div>
    </main>
  );
}
